import { useEffect } from 'react';
import { Location } from 'history';
import { get } from 'lodash';
import { MParticleEventName, trackEvent, EventType } from './elog/mparticle_tracker';
import { IUser } from './components/user_context_provider';
import { useUser } from './user_hooks';
import { isExperimentEnabled } from './user_context_helpers';
import { formatExperimentsForEventAttributes } from './elog/mparticle';
import experiments from './experiments';
import onWindowExitTrigger from './on_window_exit_trigger';

const EMAIL_POP_UP_PAGE_PATHS = [
  '/brand/gibson',
  '/brand/gretsch',
  '/brand/roland',
  '/brand/yamaha',
  '/guide',
  '/price-guide',
  '/promo/deals-and-steals',
  '/news',
];

const EMAIL_POP_UP_UTM_SOURCES_BLOCKLIST = ['braze'];
const EMAIL_POP_UP_UTM_MEDIUMS_BLOCKLIST = ['email'];
const MARKETPLACE_PAGE_BRAND_ALLOWLIST = ['fender'];

const checkUtmParams = (query: Record<string, any>): boolean => {
  const utmSource = get(query, 'utmSource', '');
  const utmMedium = get(query, 'utmMedium', '');

  const utmSourceAllowed = !EMAIL_POP_UP_UTM_SOURCES_BLOCKLIST.includes(utmSource);
  const utmMediumAllowed = !EMAIL_POP_UP_UTM_MEDIUMS_BLOCKLIST.includes(utmMedium);

  return utmSourceAllowed && utmMediumAllowed;
};

const checkMarketplaceQuery = (pathname: string, query: Record<string, any>): boolean => {
  const marketplaceBrandParam = get(query, 'make', '');
  const marketplaceBrandParamAllowed = MARKETPLACE_PAGE_BRAND_ALLOWLIST.includes(marketplaceBrandParam);

  return pathname.includes('marketplace') && marketplaceBrandParamAllowed;
};

export const pageShouldShowEmailPopUp = ({ pathname, query }: Location): boolean => {
  const isMarketplacePathWithAllowedQuery = checkMarketplaceQuery(pathname, query);
  const isAllowedPath = EMAIL_POP_UP_PAGE_PATHS.some(e => pathname.includes(e));
  const utmParamsAllowed = checkUtmParams(query);

  return utmParamsAllowed && (isAllowedPath || isMarketplacePathWithAllowedQuery);
};

const buildPopUpEvent = (user: Partial<IUser>) => ({
  eventName: MParticleEventName.EligibleForEmailPopUp,
  eventType: EventType.Other,
  experiments: formatExperimentsForEventAttributes(user, [experiments.EMAIL_WEB_CAPTURE_POP_UP]),
});

export function addOnExitTriggerForEmailPopUp(user: Partial<IUser>): void {
  if (!isExperimentEnabled(user, experiments.TURN_ON_EMAIL_POP_UP)) return;

  const popUpEvent = buildPopUpEvent(user);
  onWindowExitTrigger(() => trackEvent(popUpEvent));
}

export function AddOnExitTriggerforEmailPopUpHook({ params }) {
  const user = useUser();

  useEffect(() => {
    const utmSource = params.utm_source;
    const utmMedium = params.utm_medium;
    const utmParamsAllowed = checkUtmParams({ utmSource, utmMedium });

    if (utmParamsAllowed) {
      addOnExitTriggerForEmailPopUp(user);
    }
  });

  return null;
}
